/**
 * Constants for the "Discounts for New Visitors from Meta" smoke test
 * (RFC PROD/6140854664). Mobile-web only, US, geo-service landing pages.
 *
 * The Statsig experiment name + arm must stay in lockstep with the backend
 * (`marketing/meta_discount/constants.py`) so the same visitor is bucketed
 * consistently on `rover_cid` at view time (this CTA) and at signup time (the
 * coupon-minting receiver).
 */

// The Statsig experiment is a plain random control/variant holdback split on
// `rover_cid`. It does NOT know the visitor came from Meta — Meta targeting is
// done in code off the landing UTM cookie below (mirrors the backend).
export const META_DISCOUNT_EXPERIMENT_NAME = 'meta_discount_smoke_test';
export const META_DISCOUNT_ARM_PARAM = 'arm';
export const META_DISCOUNT_TREATMENT_ARM = 'variant';
export const META_DISCOUNT_CONTROL_ARM = 'control';

/**
 * Offer amount, in whole USD. Kept in lockstep with the backend
 * (`META_DISCOUNT_AMOUNT` in `marketing/meta_discount/constants.py`), which is
 * what actually mints the coupon — this constant only drives the display copy
 * and illustration, so the surfaced amount stays consistent with what gets
 * granted. US-only smoke test, so a bare `$` label is sufficient (no currency
 * i18n).
 */
export const META_DISCOUNT_AMOUNT = 20;
export const META_DISCOUNT_CURRENCY_SYMBOL = '$';
export const META_DISCOUNT_AMOUNT_LABEL = `${META_DISCOUNT_CURRENCY_SYMBOL}${META_DISCOUNT_AMOUNT}`;

/**
 * Meta traffic gate. The geo landing page is the page the Meta ad links to, so
 * the `utm_source` is right there in the page URL — we read it from the query
 * string to scope the offer (and its Statsig exposure) to Meta visitors. The
 * backend re-derives the same value at signup from the session-captured UTM
 * (`marketing/meta_discount/constants.py`); the value set is kept in lockstep.
 * "facebook" is Rover's existing convention; "instagram" covers IG placements.
 */
export const META_DISCOUNT_UTM_SOURCE_PARAM = 'utm_source';
export const META_DISCOUNT_META_UTM_SOURCES = ['facebook', 'instagram'];

/**
 * The offer is scoped to a specific Meta campaign, not just any facebook/instagram
 * traffic. Kept in lockstep with the backend (`META_DISCOUNT_META_UTM_CAMPAIGN` in
 * `marketing/meta_discount/constants.py`) so the landing CTA and the signup
 * receiver gate on the same campaign. Gating on the campaign also guarantees the
 * backend can re-derive Meta traffic at signup: `ReferralTrackingMiddleware` only
 * persists the landing UTM to the session when `utm_campaign` is present.
 *
 */
export const META_DISCOUNT_UTM_CAMPAIGN_PARAM = 'utm_campaign';
export const META_DISCOUNT_META_UTM_CAMPAIGN = '52507160586014';

/**
 * Query param appended to the post-signup return URL when a visitor takes the
 * offer. On return to the geo-service page we read it to show the one-time
 * green confirmation banner — this avoids re-reading the experiment (and
 * logging a spurious exposure) for authenticated users.
 */
export const META_DISCOUNT_CLAIM_PARAM = 'meta_discount';
export const META_DISCOUNT_CLAIM_VALUE = 'claimed';

export const META_DISCOUNT_SHEET_DISMISSED_KEY = 'meta_discount_sheet_dismissed';

// Marketing-owned redirect (not an app route) for the coupon's legal terms,
// linked from the "Terms apply" text in the bottom-sheet offer.
export const META_DISCOUNT_TERMS_URL = 'https://get.rover.com/rovercouponterms';

// Analytics event names (RFC "Event tracking" section).
export const EVENT_BOTTOM_SHEET_SHOW = 'discount_smoke_test_bottom_sheet_show';
export const EVENT_BOTTOM_SHEET_DISMISSED = 'discount_smoke_test_bottom_sheet_dismissed';
export const EVENT_BOTTOM_SHEET_CTA_CLICK = 'discount_smoke_test_bottom_sheet_cta_click';
export const EVENT_BOTTOM_SHEET_COLLAPSED_CTA_CLICK =
  'discount_smoke_test_bottom_sheet_collapsed_cta_click';
export const EVENT_CONFIRMATION_TOAST_SHOW = 'discount_smoke_test_confirmation_toast_show';
